import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import { useSLocations } from "../../hooks/queries";
import { useCreateLocation, useUpdateLocation } from "../../hooks/mutation";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const AirportNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [airport, setAirport] = useState(null);
  const [search, setSearch] = useState(null);
  const { data } = useSLocations(isEdit, search, page);
  const [airportsData, setAirportsData] = useState();
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (data) {
      // Filter only airports (LocationTypeID = 2)
      const airportData = data.data.filter((item) => item.LocationTypeID === 2);
      setAirportsData(airportData);
      setTotalCount(airportData.length);
    }
  }, [data]);

  const [companies, setCompanies] = useState([]);
  const [airportLocationType, setAirportLocationType] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: "",
      LocationTypeID: "",
      Code: "",
      Description: "",
      MinutesFromAirport: "",
    },
  });

  useEffect(() => {
    if (airport) {
      reset({
        CompanyID: airport.CompanyID,
        LocationTypeID: airport.LocationTypeID,
        Code: airport.Code,
        Description: airport.Description,
        MinutesFromAirport: airport.MinutesFromAirport,
      });
    }
  }, [airport, reset]);

  useEffect(() => {
    const fetchAirportData = async () => {
      const { data: Companies, error: cError } = await supabase
        .from("Companies")
        .select("CompanyID, Name");

      // Find airport location type (LocationTypeID = 2)
      const { data: LocationTypes, error: LError } = await supabase
        .from("LocationTypes")
        .select("LocationTypeID, Code, Description")
        .eq("LocationTypeID", 2);

      if (cError) {
        setCompanies([]);
      } else if (LError) {
        setAirportLocationType(null);
      } else {
        setCompanies(Companies);
        setAirportLocationType(LocationTypes[0]);
        reset({
          CompanyID: Companies[0] ? Companies[0].CompanyID : "",
          LocationTypeID: LocationTypes[0]
            ? LocationTypes[0].LocationTypeID
            : "",
          Code: "",
          Description: "",
          MinutesFromAirport: "",
        });
      }
    };
    fetchAirportData();
  }, [reset]);

  useEffect(() => {
    const fetchAirports = async () => {
      const { data: Airports, error: aError } = await supabase
        .from("Locations")
        .select(
          `
          LocationID,
          created_at,
          Code,
          Description,
          MinutesFromAirport,
          Company:CompanyID (
            CompanyID,
            Name
          ),
          LocationType:LocationTypeID (
            LocationTypeID,
            Code
          )
        `
        )
        .eq("LocationTypeID", 2)
        .order("created_at", { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (aError) {
        setAirportsData([]);
      } else {
        setAirportsData(Airports || []);
      }
    };
    fetchAirports();
  }, [page]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => old - 1);
  }, []);

  const onSuccess = useCallback((_data) => {
    toast.success("Success");
  }, []);
  const onError = useCallback((err) => {
    console.log("error", err);
  }, []);

  const onAddNew = () => {
    reset({
      CompanyID: companies[0] ? companies[0].CompanyID : "",
      LocationTypeID: airportLocationType
        ? airportLocationType.LocationTypeID
        : "",
      Code: "",
      Description: "",
      MinutesFromAirport: "",
    });
    setIsEdit(true);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setAirport();
  };

  const { mutate: createLocation, isPending: isCreating } = useCreateLocation(
    onSuccess,
    onError
  );

  const { mutate: updateLocation, isPending: isUpdating } = useUpdateLocation(
    onSuccess,
    onError
  );

  const OnCreate = useCallback(
    (data) => {
      if (airport) {
        updateLocation({ ...data, ID: airport.LocationID });
      } else {
        createLocation(data);
      }
    },
    [airport, createLocation, updateLocation]
  );

  const onEdit = (airportData) => {
    setAirport(airportData);
    setIsEdit(true);
  };

  const onDelete = async (airportId) => {
    if (window.confirm("Are you sure you want to delete this airport?")) {
      const { error } = await supabase
        .from("Locations")
        .delete()
        .eq("LocationID", airportId);

      if (error) {
        toast.error("Error deleting airport");
      } else {
        toast.success("Airport deleted successfully");
        // Refresh the list
        window.location.reload();
      }
    }
  };

  return (
    <div>
      <div className="flex">
        <h5 className="mb-0">Airport Management</h5>
        <button
          className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
          onClick={onAddNew}
        >
          <span>Add New Airport</span>
        </button>
      </div>

      <SearchBar setSearch={setSearch} OnSearch={() => {}} />

      {isEdit && (
        <div className="mt-4 p-4 border rounded-lg bg-gray-50">
          <h6 className="mb-3">Airport Details</h6>
          <form onSubmit={handleSubmit(OnCreate)}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Company
                </label>
                <Controller
                  control={control}
                  name="CompanyID"
                  rules={{ required: "Company is required" }}
                  render={({ field: { value, onChange } }) => (
                    <select
                      className="custom-input rounded-lg w-full"
                      value={value}
                      onChange={onChange}
                    >
                      <option value="">Select Company</option>
                      {companies.map((company) => (
                        <option
                          key={company.CompanyID}
                          value={company.CompanyID}
                        >
                          {company.Name}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.CompanyID && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.CompanyID.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Airport Code
                </label>
                <Controller
                  control={control}
                  name="Code"
                  rules={{ required: "Airport code is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="e.g., LAX, JFK"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
                {errors.Code && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.Code.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <Controller
                  control={control}
                  name="Description"
                  rules={{ required: "Description is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="e.g., Los Angeles International Airport"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
                {errors.Description && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.Description.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Minutes from Airport
                </label>
                <Controller
                  control={control}
                  name="MinutesFromAirport"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="0"
                      type="number"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="submit"
                disabled={isCreating || isUpdating}
                className="justify-between p-2 pr-20 pl-20 rounded-lg flex items-center custom-blue-button border-none"
              >
                <span>{airport ? "Update" : "Create"}</span>
              </button>
              <button
                type="button"
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mt-4">
        <table className="table-auto w-full border border-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 border">ID</th>
              <th className="px-4 py-2 border">Code</th>
              <th className="px-4 py-2 border">Description</th>
              <th className="px-4 py-2 border">Company</th>
              <th className="px-4 py-2 border">Minutes from Airport</th>
              <th className="px-4 py-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {airportsData?.map((airport) => (
              <tr key={airport.LocationID} className="hover:bg-gray-50">
                <td className="px-4 py-2 border">{airport.LocationID}</td>
                <td className="px-4 py-2 border">{airport.Code}</td>
                <td className="px-4 py-2 border">{airport.Description}</td>
                <td className="px-4 py-2 border">{airport.Company?.Name}</td>
                <td className="px-4 py-2 border">
                  {airport.MinutesFromAirport}
                </td>
                <td className="px-4 py-2 border">
                  <div className="flex gap-2">
                    <button
                      onClick={() => onEdit(airport)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => onDelete(airport.LocationID)}
                      className="p-1 text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination-container justify-start mt-4">
          <button
            onClick={onPrevPage}
            disabled={page === 1}
            className="pagination-button"
          >
            <FaAngleLeft className="pagination-icon" />
          </button>

          <span className="mx-4">
            Page {page} of {Math.ceil(totalCount / PAGE_SIZE)}
          </span>

          <button
            onClick={onNextPage}
            disabled={page * PAGE_SIZE >= totalCount}
            className="pagination-button"
          >
            <FaAngleRight className="pagination-icon" />
          </button>
        </div>
      </div>
    </div>
  );
};

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "Code",
      which: "contains",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center gap-4 condition-bar">
      <div className="flex items-center">
        <p className="mb-0">Look For:</p>
        <div className="flex justify-between w-10 ml-1">
          <Controller
            control={searchControl}
            name="type"
            render={({ field: { value, onChange } }) => (
              <select
                className="custom-input rounded-lg cursor-pointer"
                value={value}
                onChange={onChange}
              >
                <option value={"LocationID"}>LocationID</option>
                <option value={"Name"}>Company</option>
                <option value={"Code"}>Code</option>
                <option value={"Description"}>Description</option>
                <option value={"MinutesFromAirport"}>MinutesFromAirport</option>
              </select>
            )}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <p className="mb-0">Which:</p>
        <Controller
          control={searchControl}
          name="which"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"equals"}>Equals</option>
              <option value={"contains"}>Contains</option>
              <option value={"starts with"}>Starts With</option>
              <option value={"ends with"}>Ends With</option>
            </select>
          )}
        />
      </div>

      <div className="flex items-center gap-2">
        <Controller
          control={searchControl}
          name="text"
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              className="custom-input rounded-lg"
              value={value}
              onChange={onChange}
              placeholder="Search text"
            />
          )}
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSearchSubmit((data) => {
            setSearch(data);
            OnSearch();
          })}
          className="justify-between rounded-lg flex items-center custom-search-button border-none"
        >
          <span>Search</span>
        </button>
        <button
          onClick={() => {
            searchReset();
            setSearch(null);
          }}
          className="justify-between rounded-lg flex items-center custom-reset-button"
        >
          <span>Reset</span>
        </button>
      </div>
    </div>
  );
};

export default AirportNew;
